import LabelledDropdown from 'components/atoms/Dropdown/LabelledDropdown';
import { concat, filter, includes, isEmpty, orderBy } from 'es-toolkit/compat';
import { MainPageFilterKey } from 'interfaces/cohort';
import { FlowClassName } from 'interfaces/experimentResults';
import { getCancerSubTypes, useProceduresFieldsContext } from 'interfaces/procedure/fields/helpers';
import React from 'react';
import { Approval } from 'services/filters';
import OrchestrationIdAutoComplete from '../AdvancedSearch/OrchestrationIdAutoComplete';
import CancerSubtypesAutocomplete from '../CancerSubtypesField';
import useMainFilters, { ALL_STUDIES_VALUE, NO_STUDY_VALUE, PENDING_SLIDES_VALUE } from '../hooks/useMainFilters';
import useOrchestrationFilter from '../hooks/useOrchestrationFilter';
import useSearchFiltersFromQueryParams from '../hooks/useSearchFiltersFromQueryParams';
import AutocompleteFilter from './AutocompleteFilter';
import FullSearch from './FullSearch/FullSearch';

const autocompleteFilterKeys = [MainPageFilterKey.cancerTypes, MainPageFilterKey.stains];
const topLevelStudyValues = [ALL_STUDIES_VALUE, NO_STUDY_VALUE, PENDING_SLIDES_VALUE];

export const getOrderedStudyOptions = (allStudyOptions: any[] | null, isDrawer?: boolean) => {
  const visibleOptions = filter(allStudyOptions, (option) => !isDrawer || option.value !== PENDING_SLIDES_VALUE);
  const topLevelOptions = filter(visibleOptions, (option) => includes(topLevelStudyValues, option.value));
  const studyOptions = orderBy(
    filter(visibleOptions, (option) => !includes(topLevelStudyValues, option.value)),
    'text'
  );

  return {
    options: concat(topLevelOptions, studyOptions),
    separatorBeforeOptionIndex: topLevelOptions.length,
  };
};

export const getStudyFilterValue = ({
  studiesLoading,
  pendingSlidesMode,
  filterValue,
}: {
  studiesLoading: boolean;
  pendingSlidesMode?: boolean;
  filterValue: any;
}) => {
  if (studiesLoading) return '';
  return pendingSlidesMode && !filterValue ? PENDING_SLIDES_VALUE : filterValue;
};

interface Props {
  filterKey: string;
  filterValue: any;
  onChange: (key: string, value: any) => void;
  controlledValues?: Record<string, any>;
  label?: string;
  onTextFieldChange?: () => void;
  handleApprovalFieldChange?: (value: Approval) => void;
  enableQueries?: boolean;
  isDrawer?: boolean;
}

const MainFilter = ({ filterKey, filterValue, onChange, controlledValues, label, enableQueries, isDrawer }: Props) => {
  const { queryParams } = useSearchFiltersFromQueryParams();

  const {
    stainsOptions,
    allStudyOptions,
    cancerTypeViewOptions,
    cancerTypesLoading,
    isLoadingStainTypeOptions,
    studiesLoading,
    pendingSlidesMode,
  } = useMainFilters();

  const { options: orderedStudyOptions, separatorBeforeOptionIndex } = getOrderedStudyOptions(
    allStudyOptions,
    isDrawer
  );

  const { allCancerTypes, cancerSubtypes } = useProceduresFieldsContext();

  const { orchestrationIdOptions, orchestrationsLoading } = useOrchestrationFilter(
    queryParams.filters?.studyId,
    queryParams.resultsMode,
    [FlowClassName.CalculateFeatures]
  );

  const cancerTypesFromQueryParams = queryParams.filters?.cancerTypes || [];

  const cancerTypes: number[] = isDrawer ? controlledValues?.cancerTypes : cancerTypesFromQueryParams;

  const { relevantSubtypes, mainCancerTypeCodes } = getCancerSubTypes(cancerTypes, allCancerTypes, cancerSubtypes);

  const studyId = queryParams.filters?.studyId || 0;

  if (filterKey === MainPageFilterKey.searchTerm) {
    return (
      <FullSearch
        currentStudyId={studyId}
        enabled={enableQueries}
        isDrawer={isDrawer}
        handleChange={(event, value) => onChange(filterKey, value)}
      />
    );
  }

  if (filterKey === MainPageFilterKey.studyId) {
    return (
      <LabelledDropdown
        size="small"
        key="studyId"
        label={label}
        options={orderedStudyOptions}
        separatorBeforeOptionIndex={separatorBeforeOptionIndex}
        onOptionSelected={(optionValue) => {
          onChange(filterKey, optionValue);
        }}
        loading={studiesLoading}
        value={getStudyFilterValue({ studiesLoading, pendingSlidesMode, filterValue })}
      />
    );
  }

  if (includes(autocompleteFilterKeys, filterKey)) {
    return (
      <AutocompleteFilter
        label={label}
        options={filterKey === 'cancerTypes' ? cancerTypeViewOptions : stainsOptions}
        limitTags={2}
        handleChange={(value) => onChange(filterKey, value)}
        value={filterValue}
        loading={filterKey === 'cancerTypes' ? cancerTypesLoading : isLoadingStainTypeOptions}
      />
    );
  }

  if (filterKey === 'cancerSubtypes') {
    return (
      <CancerSubtypesAutocomplete
        multiple
        disabled={isEmpty(mainCancerTypeCodes)}
        subTree={relevantSubtypes}
        mainCancers={mainCancerTypeCodes}
        limitTags={isDrawer ? 5 : 1}
        labelMaxWidth={isDrawer ? '160px' : '90px'}
        handleChange={onChange.bind(null, 'cancerSubtypes')}
        value={filterValue}
        popperPlacement={isDrawer ? 'right' : 'bottom-start'}
      />
    );
  }

  if (filterKey === 'orchestrationId') {
    return (
      <OrchestrationIdAutoComplete
        options={orchestrationIdOptions}
        value={filterValue}
        onChange={onChange.bind(null, 'orchestrationId')}
        loading={orchestrationsLoading && Boolean(queryParams.filters?.studyId)}
      />
    );
  }
};

export default MainFilter;
